import type {FC, ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {useIsInViewport} from '@Hooks/useIsInViewport';
import {isIOS, isSafari} from 'react-device-detect';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {MediaHoverPreview} from '@Components/media-hover-preview/media-hover-preview';
import styles from './grid-media-item.module.scss';
import type {GridItemBaseStorage, GridItemProps} from '../grid-item';
import {GridItem} from '../grid-item';
import {StockScoreDebugBadge} from '@Panels/stock-panel/components/stock-score-debug-badge/stock-score-debug-badge';

export enum GridMediaItemTagType {
  COST,
  SOURCE,
  PREMIUM_SOURCE,
}

export interface CostTag {
  type: GridMediaItemTagType.COST;
  containerStyle: string;
  price: string;
  icon: string;
  textStyle: string;
}

export interface SourceTag {
  type: GridMediaItemTagType.SOURCE | GridMediaItemTagType.PREMIUM_SOURCE;
}

export type GridMediaItemTag = CostTag | SourceTag;

interface GridMediaItemProps extends GridItemProps {
  thumb?: ReactElement<any>;
  title?: string;
  className?: string;
  paddedThumb?: boolean;
  showViewportMediaOnly?: boolean;
  isImageLoaded?: boolean;
  isLoading?: boolean;
  hasGrayBackground?: boolean;
  bottomContent?: ReactElement<any>;
  hoverPreviewSrc?: string;
}

export interface GridMediaItemStorage extends GridItemBaseStorage {
  width?: number;
  height?: number;
  isLoading?: boolean;
  hoverPreviewSrc?: string;
}

/**
 * @class GridMediaItem
 */
export const GridMediaItem: FC<GridMediaItemProps> = ({paddedThumb = true, thumb = null, title = '', isImageLoaded = false, isLoading = false, ...props}) => {
  const [gridRef, setGridRef] = useState<HTMLDivElement | null>(null);
  const isInViewPort = useIsInViewport(gridRef as HTMLElement);
  const [isHoveringOnGridItem, setIsHoveringOnGridItem] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const getLoader = (): ReactElement<any> | null => {
    if (isLoading) {
      return <CircularProgressLoader color={LOADER_COLOR.WHITE} size={LOADER_SIZE.DEFAULT} classes={`${styles.loadingItem} ${styles.loaderPosition}`} />;
    }

    return null;
  };

  const getLoaderOverlay = (): ReactElement<any> | null => {
    if (isLoading) {
      return <div className={`_full-width _full-height flex-center _position-absolute ${styles.loadingOverlay}`} />;
    }

    return null;
  };

  const handleMouseEnter = (): void => {
    clearTimeout(timer.current);
    setIsHoveringOnGridItem(true);
  };

  const handleMouseLeave = (): void => {
    timer.current = window.setTimeout(() => {
      setIsHoveringOnGridItem(false);
    }, 80);
  };

  const getBottomContent = (): ReactElement<any> | null => {
    if (props.bottomContent && !isIOS && isHoveringOnGridItem) {
      return (
        <div className={'flexbox flex-items-end _position-absolute _full-width _full-height'}>
          <div className={'_full-width _position-absolute'}>{props.bottomContent}</div>
        </div>
      );
    }
    return null;
  };

  const getClasses = (): string => {
    return [
      'flex-v-row flex-center _full-width _full-height radius-12',
      isImageLoaded ? '' : styles.thumbBackground,
      props.hasTransparency ? styles.thumbWithTransparency : '',
      props.hasGrayBackground ? styles.grayBackground : '',
      isLoading ? styles.loadingContainer : '',
      'thumb',
    ].join(' ');
  };

  const getThumbElement = (): ReactElement | null => {
    if (!thumb || !(isInViewPort || !(isIOS || isSafari))) {
      return null;
    }

    const thumbElement = React.cloneElement(thumb, {
      className: `${thumb.props.className} ${styles.thumb} ${paddedThumb ? styles.paddedThumb : ''} ${isLoading ? styles.loadingItem : ''} `,
    });

    if (!props.hoverPreviewSrc) {
      return thumbElement;
    }
    return (
      <MediaHoverPreview previewSrc={props.hoverPreviewSrc} className="flex-center _full-width _full-height">
        {thumbElement}
      </MediaHoverPreview>
    );
  };

  return (
    <GridItem {...props}>
      <div className={getClasses()} ref={setGridRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {getThumbElement()}
        {getLoaderOverlay()}
        {getLoader()}
        {getBottomContent()}
        <StockScoreDebugBadge idStockMedia={props.id} />
      </div>
    </GridItem>
  );
};
